import { useEffect, useState } from "react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";

const LABELS = {
  ar: { online: "متاحون الآن", offline: "سنعاود الاتصال قريبًا" },
  en: { online: "Online now", offline: "We'll respond soon" },
  ku: { online: "ئێستا بەردەستە", offline: "بەم زووانە وەڵام دەدەینەوە" },
};

const parseHM = (s, fallback) => {
  const m = String(s || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return fallback;
  return { h: Math.min(23, parseInt(m[1], 10)), m: Math.min(59, parseInt(m[2], 10)) };
};

// Compute Baghdad local time (UTC+3, no DST) reliably regardless of visitor timezone.
const baghdadNow = () => {
  const now = new Date();
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utcMs + 3 * 60 * 60000);
};

export default function LiveStatus({ compact = false }) {
  const { lang } = useLang();
  const { settings } = useSite();
  const L = LABELS[lang] || LABELS.ar;
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000); // re-check every minute
    return () => clearInterval(id);
  }, []);

  const start = parseHM(settings.business_hours_start, { h: 9, m: 0 });
  const end = parseHM(settings.business_hours_end, { h: 17, m: 0 });
  // Working days as JS getDay indices (Sun=0, Mon=1, ..., Sat=6). Default Sat-Thu (Iraq week).
  const daysStr = String(settings.business_days || "6,0,1,2,3,4").trim();
  const workingDays = new Set(daysStr.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => n >= 0 && n <= 6));

  const now = baghdadNow();
  void tick;
  const startMin = start.h * 60 + start.m;
  const endMin = end.h * 60 + end.m;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const isWorkingDay = workingDays.has(now.getDay());
  const isOnline = isWorkingDay && nowMin >= startMin && nowMin < endMin;

  const hoursText = settings[`hours_${lang}`] || `${start.h}:${String(start.m).padStart(2, "0")}–${end.h}:${String(end.m).padStart(2, "0")}`;

  const dot = (
    <span className="relative inline-flex h-2.5 w-2.5 shrink-0">
      <span
        className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
        style={{ background: isOnline ? "#22c55e" : "#f59e0b" }}
      />
      <span
        className="relative inline-flex rounded-full h-2.5 w-2.5"
        style={{ background: isOnline ? "#22c55e" : "#f59e0b" }}
      />
    </span>
  );

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-bold border"
        style={{
          background: isOnline ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)",
          borderColor: isOnline ? "rgba(34,197,94,0.35)" : "rgba(245,158,11,0.35)",
          color: isOnline ? "#22c55e" : "#f59e0b",
        }}
        data-testid={`live-status-pill-${isOnline ? "online" : "offline"}`}
      >
        {dot}
        <span>{isOnline ? L.online : L.offline}</span>
      </span>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-2.5 rounded-full px-3.5 py-1.5 text-xs font-bold border"
      style={{
        background: isOnline ? "rgba(34,197,94,0.12)" : "rgba(245,158,11,0.12)",
        borderColor: isOnline ? "rgba(34,197,94,0.4)" : "rgba(245,158,11,0.4)",
        color: isOnline ? "#22c55e" : "#f59e0b",
      }}
      data-testid={`live-status-${isOnline ? "online" : "offline"}`}
    >
      {dot}
      <span>{isOnline ? L.online : L.offline}</span>
      {isOnline && <span className="text-slate-400 font-normal">— {hoursText}</span>}
    </div>
  );
}
